import type { Appointment } from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { featureFlags } from '@seen/core/featureFlags'
import * as appointments from '@seen/core/models/appointments/index'
import * as healthcareServices from '@seen/core/models/healthcareService/index'
import { createReference } from '@seen/core/references'
import type { QueryTypes } from '@seen/core/search'
import { parseReferenceString } from '@seen/core/utils'
import {
  IconCalendarEvent,
  IconFilterCheck,
  IconStethoscope,
  IconUser,
} from '@tabler/icons-react'
import type { ColumnDef } from '@tanstack/react-table'
import { compact, orderBy, sortBy, uniqBy } from 'lodash-es'
import { type JSX, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { AppointmentDetails } from '#src/components/appointment/AppointmentDetails.tsx'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { ResourceCard } from '#src/components/core/ResourceCard.tsx'
import { Sheet } from '#src/components/core/sheet/Sheet.tsx'
import { SheetContent } from '#src/components/core/sheet/SheetContent.tsx'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { FilterComposer } from '#src/components/filter/FilterComposer.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { buildSchedulingCalendarExpandUrl } from '#src/stores/calendar.ts'

interface AppointmentGroups {
  futureAppointments: appointments.ExtendedAppointment[]
  pastAppointments: appointments.ExtendedAppointment[]
  cancelledAppointments: appointments.ExtendedAppointment[]
}

export function AppointmentsTable({
  patientId,
}: {
  patientId?: string
}): JSX.Element {
  const [filters, setFilters] = useState<Filter[]>([])
  const location = useLocation()
  const navigate = useNavigate()
  const selectedAppointmentId = location.hash.replace('#', '')
  const setSelectedAppointmentId = (appointmentId?: string): void => {
    void navigate(`#${appointmentId ?? ''}`)
  }
  const selectedAppointmentReference = selectedAppointmentId
    ? parseReferenceString<Appointment>(`Appointment/${selectedAppointmentId}`)
    : undefined

  const rumStableActionNames = useFeatureFlag(featureFlags.rumStableActionNames)

  // Appointment.healthcareServices is a bare reference with no coded type/
  // category, so the RUM appointment-kind qualifier resolves it against the
  // full healthcare service list rather than reading `display` (see
  // buildAppointmentKind). This search exists only for that qualifier, so
  // it's gated on the flag rather than firing on every render.
  const { data: allHealthcareServices } = useSearch(
    healthcareServices.ExtendedHealthcareService,
    healthcareServices.ExtendedHealthcareService.baseSearchQuery(),
    { query: { enabled: rumStableActionNames } }
  )
  const healthcareServiceByReference = useMemo(() => {
    if (!rumStableActionNames) {
      return undefined
    }
    return new Map(
      (allHealthcareServices ?? []).map((hs) => [hs.reference.reference, hs])
    )
  }, [rumStableActionNames, allHealthcareServices])

  const getRowAttributes = (
    row: appointments.ExtendedAppointment
  ): Record<string, string> | undefined => {
    if (!rumStableActionNames) {
      return undefined
    }
    const healthcareServiceReference = row.healthcareServices[0]?.reference
    const resolvedHealthcareService = healthcareServiceReference
      ? healthcareServiceByReference?.get(healthcareServiceReference)
      : undefined
    return {
      'data-dd-action-name': 'appointments.appointment.open',
      'data-orbit-rum-appointment-kind':
        healthcareServices.buildAppointmentKind(resolvedHealthcareService),
    }
  }

  const columns = useMemo<ColumnDef<appointments.ExtendedAppointment>[]>(
    () =>
      compact([
        patientId
          ? null
          : {
              id: 'participant',
              header: 'Participant',
              cell: ({ row }) => {
                const patient = row.original.patient
                return patient ? <ResourceCard value={patient} /> : null
              },
            },
        {
          id: 'start',
          header: 'Start Time',
          cell: ({ row }) => <DateTimeDisplay value={row.original.start} />,
        },
        {
          id: 'appointmentType',
          header: 'Appointment Type',
          cell: ({ row }) => {
            const [healthcareService] = row.original.healthcareServices
            return healthcareService ? (
              <div>{referenceDisplay(healthcareService)}</div>
            ) : null
          },
        },
        {
          id: 'practitioner',
          header: 'Practitioner',
          cell: ({ row }) => {
            const [practitioner] = row.original.practitioners
            return practitioner ? (
              <PractitionerDisplay practitioner={practitioner} />
            ) : null
          },
        },
        {
          id: 'status',
          header: 'Status',
          cell: ({ row }) => <div>{row.original.status}</div>,
        },
      ]),
    [patientId]
  )

  const filterQuery = useMemo(() => {
    const baseQuery: QueryTypes = {
      _sort: '-_lastUpdated',
      _count: '1000',
      'status:not': 'entered-in-error',
    }

    if (patientId) {
      baseQuery.patient = createReference({
        resourceType: 'Patient',
        id: patientId,
      })
    }

    return baseQuery
  }, [patientId])

  // Get all appointments in one query
  const { data: allAppointments = [] } = useSearch(
    appointments.ExtendedAppointment,
    filterQuery
  )

  const participantOptions = useMemo(
    () =>
      uniqBy(
        allAppointments.flatMap((appointment) => {
          if (!appointment.patient?.reference) {
            return []
          }

          return {
            value: appointment.patient.reference,
            label:
              referenceDisplay(appointment.patient) ??
              appointment.patient.reference,
            keywords: [
              referenceDisplay(appointment.patient) ??
                appointment.patient.reference,
            ],
          }
        }),
        'value'
      ),
    [allAppointments]
  )

  const statusOptions = useMemo(
    () =>
      uniqBy(
        allAppointments.map((appointment) => ({
          value: appointment.status,
          label: appointment.status,
          keywords: [appointment.status],
        })),
        'value'
      ),
    [allAppointments]
  )

  const appointmentTypeOptions = useMemo(
    () =>
      uniqBy(
        allAppointments.flatMap((appointment) => {
          const [healthcareService] = appointment.healthcareServices
          const label = referenceDisplay(healthcareService)
          if (!label) {
            return []
          }

          return {
            value: label,
            label,
            keywords: [label],
          }
        }),
        'value'
      ),
    [allAppointments]
  )

  const practitionerOptions = useMemo(
    () =>
      uniqBy(
        allAppointments.flatMap((appointment) => {
          const [practitioner] = appointment.practitioners
          if (!practitioner?.reference) {
            return []
          }

          return {
            value: practitioner.reference,
            label: referenceDisplay(practitioner) ?? practitioner.reference,
            keywords: [
              referenceDisplay(practitioner) ?? practitioner.reference,
            ],
          }
        }),
        'value'
      ),
    [allAppointments]
  )

  const filterOptions = useMemo<
    FilterConfigMap<appointments.ExtendedAppointment>
  >(() => {
    const participantFilter: FilterConfigMap<appointments.ExtendedAppointment> =
      {}
    if (!patientId) {
      participantFilter.participant = {
        label: 'Participant',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: participantOptions,
        valueFn: (appointment) => appointment.patient?.reference ?? '',
      }
    }
    return {
      ...participantFilter,
      status: {
        label: 'Status',
        icon: <IconFilterCheck className="size-4" />,
        type: 'multi-select',
        options: statusOptions,
        valueFn: (appointment) => appointment.status,
      },
      appointmentType: {
        label: 'Appointment Type',
        icon: <IconStethoscope className="size-4" />,
        type: 'multi-select',
        options: appointmentTypeOptions,
        valueFn: (appointment) =>
          referenceDisplay(appointment.healthcareServices[0]) ?? '',
      },
      practitioner: {
        label: 'Practitioner',
        icon: <IconUser className="size-4" />,
        type: 'multi-select',
        options: practitionerOptions,
        valueFn: (appointment) => appointment.practitioners[0]?.reference ?? '',
      },
      startDate: {
        label: 'Start Date',
        icon: <IconCalendarEvent className="size-4" />,
        type: 'date',
        valueFn: (appointment) => appointment.start ?? '',
      },
    }
  }, [
    appointmentTypeOptions,
    participantOptions,
    patientId,
    practitionerOptions,
    statusOptions,
  ])

  const filteredAppointments = useFilteredData(
    allAppointments,
    filterOptions,
    filters
  )

  const { futureAppointments, pastAppointments, cancelledAppointments } =
    useMemo(
      () =>
        filteredAppointments.reduce<AppointmentGroups>(
          (
            acc: AppointmentGroups,
            appointment: appointments.ExtendedAppointment
          ) => {
            if (appointment.isCancelled) {
              acc.cancelledAppointments.push(appointment)
            } else if (!appointment.isPast) {
              acc.futureAppointments.push(appointment)
            } else {
              acc.pastAppointments.push(appointment)
            }
            return acc
          },
          {
            futureAppointments: [],
            pastAppointments: [],
            cancelledAppointments: [],
          }
        ),
      [filteredAppointments]
    )

  // Sort appointments
  const sortedFutureAppointments = useMemo(
    () =>
      sortBy(futureAppointments, (appointment) =>
        appointment.start ? new Date(appointment.start).getTime() : 0
      ),
    [futureAppointments]
  )

  const sortedPastAppointments = useMemo(
    () =>
      orderBy(
        pastAppointments,
        (appointment) =>
          appointment.start ? new Date(appointment.start).getTime() : 0,
        'desc'
      ),
    [pastAppointments]
  )

  const sortedCancelledAppointments = useMemo(
    () =>
      orderBy(
        cancelledAppointments,
        (appointment) =>
          appointment.start ? new Date(appointment.start).getTime() : 0,
        'desc'
      ),
    [cancelledAppointments]
  )

  const selectedAppointment = useMemo(
    () => allAppointments.find((a) => a.id === selectedAppointmentId),
    [allAppointments, selectedAppointmentId]
  )

  return (
    <div className="flex flex-col gap-8">
      <FilterComposer<appointments.ExtendedAppointment>
        id="appointments-table-filters"
        filterOptions={filterOptions}
        onChange={setFilters}
      />
      <div>
        <h2 className="mb-4 text-lg font-semibold">Upcoming</h2>
        <DataTable<appointments.ExtendedAppointment, unknown>
          columns={columns}
          data={sortedFutureAppointments}
          onRowClick={(row) => setSelectedAppointmentId(row.id)}
          getRowAttributes={getRowAttributes}
        />
      </div>

      <div>
        <h2 className="mb-4 text-lg font-semibold">Past</h2>
        <DataTable<appointments.ExtendedAppointment, unknown>
          columns={columns}
          data={sortedPastAppointments}
          onRowClick={(row) => setSelectedAppointmentId(row.id)}
          getRowAttributes={getRowAttributes}
        />
      </div>

      {sortedCancelledAppointments.length > 0 && (
        <div>
          <h2 className="mb-4 text-lg font-semibold">Cancelled</h2>
          <DataTable<appointments.ExtendedAppointment, unknown>
            columns={columns}
            data={sortedCancelledAppointments}
            onRowClick={(row) => setSelectedAppointmentId(row.id)}
            getRowAttributes={getRowAttributes}
          />
        </div>
      )}

      <Sheet
        open={!!selectedAppointmentId}
        onOpenChange={(open) => {
          if (!open) {
            void navigate('', { replace: true })
          }
        }}
      >
        <SheetContent side="right" className="max-w-xl">
          {selectedAppointmentReference && (
            <AppointmentDetails
              appointmentRef={selectedAppointmentReference}
              onClickFocusDateTime={
                selectedAppointment
                  ? () => {
                      void navigate(
                        buildSchedulingCalendarExpandUrl(selectedAppointment)
                      )
                    }
                  : undefined
              }
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
